import './calendar-header.scss';
import type { RefObject } from 'react';
import { MONTH_OPTIONS } from '../../constants/calendar';

type CalendarHeaderProps = {
  usesWeeklyView: boolean;
  viewDate: Date;
  openPicker: 'month' | 'year' | null;
  pickerRef: RefObject<HTMLDivElement | null>;
  yearOptions: number[];
  weekRangeParts: { range: string; year: string };
  isViewingCurrentPeriod: boolean;
  onChangePeriod: (direction: -1 | 1) => void;
  onTogglePicker: (picker: 'month' | 'year') => void;
  onMonthChange: (month: number) => void;
  onYearChange: (year: number) => void;
  onGoToToday: () => void;
};

const CalendarHeader = ({
  usesWeeklyView,
  viewDate,
  openPicker,
  pickerRef,
  yearOptions,
  weekRangeParts,
  isViewingCurrentPeriod,
  onChangePeriod,
  onTogglePicker,
  onMonthChange,
  onYearChange,
  onGoToToday,
}: CalendarHeaderProps) => (
  <>
    <div className="meeting-calendar__header">
      <button
        type="button"
        className="meeting-calendar__nav"
        onClick={() => onChangePeriod(-1)}
        aria-label={usesWeeklyView ? 'Previous week' : 'Previous month'}
      >
        ‹
      </button>

      {!usesWeeklyView ? (
        <div className="meeting-calendar__month-year-picker" ref={pickerRef}>
          <div className="meeting-calendar__picker-group">
            <button
              type="button"
              className="meeting-calendar__picker-button meeting-calendar__month-select"
              onClick={() => onTogglePicker('month')}
              aria-label="Select month"
              aria-haspopup="listbox"
              aria-expanded={openPicker === 'month'}
            >
              <span>{MONTH_OPTIONS[viewDate.getMonth()]}</span>
              <span className="meeting-calendar__picker-arrow" aria-hidden="true">
                ▾
              </span>
            </button>
            {openPicker === 'month' && (
              <div className="meeting-calendar__picker-menu" role="listbox" aria-label="Month options">
                {MONTH_OPTIONS.map((month, index) => (
                  <button
                    key={month}
                    type="button"
                    role="option"
                    aria-selected={index === viewDate.getMonth()}
                    className={`meeting-calendar__picker-item ${
                      index === viewDate.getMonth() ? 'is-selected' : ''
                    }`}
                    onClick={() => onMonthChange(index)}
                  >
                    {month}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="meeting-calendar__picker-group">
            <button
              type="button"
              className="meeting-calendar__picker-button meeting-calendar__year-select"
              onClick={() => onTogglePicker('year')}
              aria-label="Select year"
              aria-haspopup="listbox"
              aria-expanded={openPicker === 'year'}
            >
              <span>{viewDate.getFullYear()}</span>
              <span className="meeting-calendar__picker-arrow" aria-hidden="true">
                ▾
              </span>
            </button>
            {openPicker === 'year' && (
              <div className="meeting-calendar__picker-menu" role="listbox" aria-label="Year options">
                {yearOptions.map((year) => (
                  <button
                    key={year}
                    type="button"
                    role="option"
                    aria-selected={year === viewDate.getFullYear()}
                    className={`meeting-calendar__picker-item ${
                      year === viewDate.getFullYear() ? 'is-selected' : ''
                    }`}
                    onClick={() => onYearChange(year)}
                  >
                    {year}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="meeting-calendar__picker-group meeting-calendar__picker-group--compact" ref={pickerRef}>
          <button
            type="button"
            className="meeting-calendar__picker-button meeting-calendar__week-range-button"
            onClick={() => onTogglePicker('year')}
            aria-label="Select year"
            aria-haspopup="listbox"
            aria-expanded={openPicker === 'year'}
          >
            <span className="meeting-calendar__week-range">{weekRangeParts.range}</span>
            <span className="meeting-calendar__week-year">{weekRangeParts.year}</span>
            <span className="meeting-calendar__picker-arrow" aria-hidden="true">
              ▾
            </span>
          </button>
          {openPicker === 'year' && (
            <div className="meeting-calendar__picker-menu" role="listbox" aria-label="Year options">
              {yearOptions.map((year) => (
                <button
                  key={year}
                  type="button"
                  role="option"
                  aria-selected={year === viewDate.getFullYear()}
                  className={`meeting-calendar__picker-item ${
                    year === viewDate.getFullYear() ? 'is-selected' : ''
                  }`}
                  onClick={() => onYearChange(year)}
                >
                  {year}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <button
        type="button"
        className="meeting-calendar__nav"
        onClick={() => onChangePeriod(1)}
        aria-label={usesWeeklyView ? 'Next week' : 'Next month'}
      >
        ›
      </button>
    </div>

    <div className="meeting-calendar__today-row">
      <button
        type="button"
        className={`meeting-calendar__today ${
          isViewingCurrentPeriod ? 'meeting-calendar__today--current' : 'meeting-calendar__today--away'
        }`}
        onClick={onGoToToday}
      >
        Today
      </button>
    </div>
  </>
);

export default CalendarHeader;
